import { Platform, TextInput, View } from "react-native";
import {
  Box,
  resolveAHTextStyle,
  useAHListItemStyle,
  useLayoutContext,
  useScaleFactor,
  useTheme,
} from "arkh";
import { HeaderButton, HeaderButtonLoading } from "../../components";
import React, { useCallback, useLayoutEffect } from "react";
import { useIntl } from "react-intl";
import { graphql, useMutation } from "react-relay";
import { useAlert } from "../../common/Alert";
import { ProfileStackScreenProps } from "./ProfileStack";

export const EditNameScreen = (props: ProfileStackScreenProps<"EditName">) => {
  const intl = useIntl();
  const { setParams, goBack, setOptions } = props.navigation;
  const { name, userId } = props.route.params;
  const theme = useTheme();

  const Alert = useAlert();
  const [updateName, loading] = useMutation(graphql`
    mutation EditNameScreenMutation($name: String!) {
      me {
        setName(name: $name) {
          id
          name
        }
      }
    }
  `);

  const onUpdateName = useCallback(() => {
    updateName({
      variables: {
        name,
      },
      onCompleted: () => {
        goBack();
      },
      onError: () => {
        Alert.error(
          intl.formatMessage({
            defaultMessage: "Failed save",
            description: "MyProfile:EditName: failed to edit name alert title",
          }),
          intl.formatMessage({
            defaultMessage: "The new name could not be saved at the moment",
            description:
              "MyProfile:EditName: failed to edit name alert description",
          })
        );
      },
      optimisticResponse: {
        me: {
          __typename: "MeMutation",
          setName: {
            __typename: "User",
            id: userId,
            name,
          },
        },
      },
    });
  }, [Alert, goBack, intl, name, updateName, userId]);

  useLayoutEffect(() => {
    setOptions({
      headerLeft:
        Platform.OS === "ios"
          ? () => <HeaderButton type="CANCEL" onPress={goBack} />
          : undefined,
      headerTitle: intl.formatMessage({
        defaultMessage: "Your name",
        description: "MyProfile:EditName:header title",
      }),
      headerRight: () => (
        <>
          {loading ? (
            <HeaderButtonLoading />
          ) : (
            <HeaderButton type="SAVE" onPress={onUpdateName} />
          )}
        </>
      ),
    });
  }, [goBack, intl, loading, onUpdateName, setOptions, updateName]);

  const layout = useLayoutContext();
  const listItemStyle = useAHListItemStyle();
  const sf = useScaleFactor();
  return (
    <Box
      space="medium"
      style={{
        flex: 1,
      }}
    >
      <View
        style={[
          listItemStyle.container,
          listItemStyle.topItem,
          listItemStyle.bottomItem,
        ]}
      >
        <TextInput
          placeholder="Your name"
          autoFocus
          onSubmitEditing={onUpdateName}
          style={[
            resolveAHTextStyle("body-big", sf, theme, layout),
            {
              flex: 1,
              textAlign: "center",
              flexDirection: "row",
              flexWrap: "wrap",
            },
          ]}
          value={name}
          returnKeyType="done"
          onChangeText={(name) => setParams({ name })}
        />
      </View>
    </Box>
  );
};
